import {
    LayoutDashboard,
    Cable,
    Receipt,
    SlidersHorizontal,
    Activity,
    ScrollText,
    Database,
    ListChecks,
    Map,
    Users, Gauge, Energy, EvCharger, Building, Shield, Cpu, Settings, Zap, DollarSign,
} from "@/components/icons";
import React from "react";

export interface NavItem {
    to: string;
    /** Catalog key of the visible label; translated at render, never used as an identity. */
    labelKey: string;
    Icon: React.ElementType;
    adminOnly?: boolean;
    orgUserAllowed?: boolean;
    // Hidden from a read-only admin: a mutate-only surface (e.g. the emulator)
    // that a viewer cannot use (the backend forbids its writes outright).
    fullAdminOnly?: boolean;
    tierRequired?: "prime";
    // External targets (e.g. the legacy /console UI served by Flask) bypass
    // React Router and use a regular <a href> so the browser does a full
    // navigation out of the SPA.
    external?: boolean;
}

export const nav: NavItem[] = [
    { to: "/", labelKey: "operator.nav.dashboard", Icon: Gauge },
    { to: "/devices", labelKey: "operator.nav.devices", Icon: EvCharger },
    { to: "/sessions", labelKey: "operator.nav.sessions", Icon: Energy },
    { to: "/billing", labelKey: "operator.nav.billing", Icon: Receipt, adminOnly: true },
    // Load balancing: an org manager configures circuit groups within their own
    // org(s); a system admin manages all. Hidden from drivers. (Backend scopes each
    // group/unit to the manager's organization.)
    { to: "/load-balancing", labelKey: "operator.nav.load_balancing", Icon: SlidersHorizontal, adminOnly: true, orgUserAllowed: true },
    { to: "/admin/dashboard", labelKey: "operator.nav.admin_panel", Icon: LayoutDashboard, adminOnly: true },
    { to: "/metrics", labelKey: "operator.nav.metrics", Icon: Activity, adminOnly: true },
    { to: "/system-health", labelKey: "operator.nav.system_health", Icon: ListChecks, adminOnly: true },
    { to: "/mobile-versions", labelKey: "operator.nav.mobile_versions", Icon: Activity, adminOnly: true },
    { to: "/cache-stats", labelKey: "operator.nav.cache_stats", Icon: Database, adminOnly: true },
    { to: "/logs", labelKey: "operator.nav.logs", Icon: ScrollText, adminOnly: true },
    { to: "/ecache", labelKey: "operator.nav.ecache", Icon: Database, adminOnly: true },
    { to: "/eventlog", labelKey: "operator.nav.event_log", Icon: Cable, adminOnly: true },
    { to: "/audit", labelKey: "operator.nav.audit_log", Icon: ScrollText, adminOnly: true },
    { to: "/map", labelKey: "operator.nav.map", Icon: Map, adminOnly: true },
    { to: "/users", labelKey: "operator.nav.users", Icon: Users, adminOnly: true },
    { to: "/organizations", labelKey: "operator.nav.organizations", Icon: Building, adminOnly: true, orgUserAllowed: true },
    { to: "/access-control", labelKey: "operator.nav.access_control", Icon: Shield, adminOnly: true, orgUserAllowed: true },
    { to: "/pricing-profiles", labelKey: "operator.nav.pricing_profiles", Icon: DollarSign, adminOnly: true, orgUserAllowed: true },
    { to: "/emulator", labelKey: "operator.nav.emulator", Icon: Cpu, adminOnly: true, fullAdminOnly: true },
    { to: "/pando", labelKey: "operator.nav.pando", Icon: Zap, adminOnly: true },
    { to: "/console/", labelKey: "operator.nav.console", Icon: Settings, adminOnly: true, external: true },
];

export interface NavVisibilityCtx {
    isAdmin: boolean;    // system administrator (full write access)
    isManager: boolean;  // manager of ≥1 organization
    isFree: boolean;
    // May VIEW system-admin pages: full admin OR read-only admin. Defaults to
    // isAdmin when omitted so callers that predate the read-only role still work.
    isAdminViewer?: boolean;
}

/**
 * Whether a nav item is visible to the current user.
 *  - orgUserAllowed items are organization-MANAGEMENT pages: a full system admin
 *    or an organization manager sees them; a read-only admin does NOT (those
 *    pages depend on manager-plane endpoints a read-only admin cannot use).
 *  - other adminOnly items are system tools: any admin VIEWER (full or read-only)
 *    sees them, except fullAdminOnly items which stay full-admin-only.
 */
export function isNavItemVisible(
    item: NavItem,
    { isAdmin, isManager, isFree, isAdminViewer }: NavVisibilityCtx,
): boolean {
    const viewer = isAdminViewer ?? isAdmin;
    if (item.adminOnly) {
        if (item.fullAdminOnly && !isAdmin) {
            return false;
        }
        if (item.orgUserAllowed) {
            // Organization-management page: full admin or org manager only.
            if (!isAdmin && !isManager) return false;
        } else if (!viewer) {
            // System tool: any admin viewer.
            return false;
        }
    }
    return !(item.tierRequired === "prime" && isFree);
}
